import {
  EDropdownGET,
  EDropdownGET_CmdInstance,
  EDropdownGET_OutputColumn,
  EDropdownGET_OutputColumn_2,
  EKitRunGET_Run,
  SyncStageI,
  SyncStageII,
} from "./api-schema";
import { ERunGET } from "./api-schema";
import { LegoDoll } from "../shelves/kitShelf";

// alias for value type
export type SingleSchemaElementValue = string | number | boolean | Date | null;
export type SchemaElementValue =
  | SingleSchemaElementValue
  | SingleSchemaElementValue[];
export interface ParamOption {
  display: string;
  display2: string;
  color: string;
  icon: string;
  group: string;
  value: SchemaElementValue;
  disabled: boolean;
}
interface ParamOptionInput {
  display: string;
  display2?: string;
  color?: string;
  icon?: string;
  group?: string;
  value: SchemaElementValue;
  disabled?: boolean;
}
export function paramOption({
  display,
  display2 = "",
  color = "",
  icon = "",
  group = "",
  value,
  disabled = false,
}: ParamOptionInput): ParamOption {
  return {
    display,
    display2,
    color,
    icon,
    group,
    value,
    disabled,
  };
}

// component type
export type ComponentId =
  | "textarea"
  | "json"
  | "cardMultiselect"
  | "b-select"
  | "b-input"
  | "b-checkbox"
  | "b-datepicker"
  | "b-timepicker"
  | "b-datetimepicker"
  | "b-radio";
export interface FlatSchemaConstraints {
  minimum: string | null;
  maximum: string | null;
  step: string | null;
  maxLength: number | null;
  pattern: string | null;
  validationMessage: string | null;
}
export function initFlatSchemaConstraints(): FlatSchemaConstraints {
  return {
    minimum: null,
    maximum: null,
    step: null,
    maxLength: null,
    pattern: null,
    validationMessage: null,
  };
}
export interface FlatSchemaElement {
  key: string;
  // no typeSystem
  // no typeId
  constraints: FlatSchemaConstraints;
  // no jsonType
  component: ComponentId;
  inputType: string;
  // no objectPropertySchemas
  // no listElementSchema
  displayValue: string | null; // maps to old displayValue
  // no groupHeader
  // no componentWidth
  // no order
  // no legoInputId
  defaultAmbiguousInput: HowToHandleEmptyInput | null;
  lockDefaultValue: boolean;
  value: SchemaElementValue;
  // new stuff
  path: string;
  options: ParamOption[] | null;
  lineBreak: boolean;
  metadata: Record<string, any> | null;
  colSpan: 1 | 2 | 3 | 4 | 5 | 6;
  dropdownId: number | null;
  placeholder: string | null;
  tooltip: string | null;
}

export interface FlatSchemaElementInput {
  component: ComponentId;
  colSpan?: 1 | 2 | 3 | 4 | 5 | 6;
  value: SchemaElementValue;
  key: string;
  path: string;
  defaultAmbiguousInput?: HowToHandleEmptyInput | null;
  displayValue?: string | null; // maps to old displayValue
  options?: ParamOption[] | null;
  lineBreak?: boolean;
  metadata?: Record<string, any> | null;
  constraints?: FlatSchemaConstraints;
  lockDefaultValue?: boolean;
  inputType?: string;
  dropdownId?: number | null;
  placeholder?: string | null;
  tooltip?: string | null;
}
export function flatSchemaElement({
  component,
  colSpan = 6,
  value,
  key,
  path,
  defaultAmbiguousInput = null,
  displayValue = null,
  options = null,
  lineBreak = false,
  metadata = null,
  constraints = initFlatSchemaConstraints(),
  lockDefaultValue = false,
  inputType = "",
  dropdownId = null,
  placeholder = null,
  tooltip = null,
}: FlatSchemaElementInput): FlatSchemaElement {
  return {
    component,
    colSpan,
    value,
    key,
    path,
    defaultAmbiguousInput,
    displayValue,
    options,
    lineBreak,
    metadata,
    constraints,
    lockDefaultValue,
    inputType,
    dropdownId,
    placeholder,
    tooltip,
  };
}
export interface DisplayValue {
  display: string;
  value: string | number | Date | null;
}

export interface InternalFilterConstructor {
  options: DisplayValue[] | null;
  currentValue: any;
  computedFilter: (data: any) => boolean;
  pillDisplay: () => string;
  iconLeft: string;
  onClose: () => void;
}
export class InternalFilter {
  options: DisplayValue[] | null;
  currentValue: any;
  computedFilter: (data: any) => boolean;
  pillDisplay: () => string;
  iconLeft: string;
  onClose: () => void;

  constructor({
    options,
    currentValue,
    computedFilter,
    pillDisplay,
    iconLeft,
    onClose,
  }: InternalFilterConstructor) {
    this.options = options;
    this.currentValue = currentValue;
    this.computedFilter = computedFilter.bind(this);
    this.pillDisplay = pillDisplay.bind(this);
    this.iconLeft = iconLeft;
    this.onClose = onClose.bind(this);
  }
}

export interface InternalFilterCollection {
  [key: string]: InternalFilter;
}

export interface BulkRunPayload {
  kitInstanceId: number | null;
  runramList: any[];
}
export enum KitVisibility {
  justme = "justme",
  public = "public",
  org = "org",
  store = "store",
}
export enum ExecutionEnv {
  nol = "nol",
  sql = "sql",
  markdown = "markdown",
  variable = "variable",
}
export interface Invitation {
  orgName: string;
  fromOrgId: string;
  toEmail: string;
  fromUserId: string;
  role: string;
  fromName: string;
  fromNickname: string;
  fromEmail: string;
  status: string;
}

export class FlankUser {
  userId: string;
  nickname?: string;
  name?: string;
  email?: string;
  pictureUrl: string;

  constructor(user: any) {
    this.userId = user.userId;
    this.nickname = user?.nickname ?? null;
    this.name = user?.name ?? null;
    this.email = user?.email ?? null;
    this.pictureUrl = user?.pictureUrl ?? null;
  }
}

export enum ColumnsKeys {
  name = "name",
  lastRun = "lastRun",
  status = "status",
  runners = "runners",
  // sharers = "sharers",
  runButtons = "runButtons",
}
export interface SchemaConstraints {
  multipleOf?: number | null;
  maximum?: string | null;
  exclusiveMaximum?: boolean | null;
  minimum?: string | null;
  exclusiveMinimum?: boolean | null;
  maxLength?: number | null;
  minLength?: number | null;
  pattern?: string | null;
  maxItems?: number | null;
  minItems?: number | null;
  uniqueItems?: boolean | null;
  maxProperties?: number | null;
  minProperties?: number | null;
  required?: boolean | null;
  readOnly?: boolean | null;
  writeOnly?: boolean | null;
  nullable?: boolean | null;
  validationMessage?: string | null;
  step?: string | null;
}

export type GeneralKVPair = {
  key: any;
  value: any;
};

export type LegoSchemaElement = {
  // IMPORTANT NOTE: See params.initElement if you're making any changes here
  // sync, global across orgs, lego-level, (in most settings) read-only
  key: string;
  typeSystem: string; // should be deprecated at some point...
  typeId: string;
  constraints: SchemaConstraints;
  jsonType: string; // This should be calculated... isn't used in backend at all
  component: string;
  inputType: string; // These two should be consolidated
  objectPropertySchemas?: KitRunSchemaElement[] | LegoSchemaElement[] | null;
  listElementSchema?: LegoSchemaElement | null;
  displayValue: string | null;
  groupHeader: string | null;
  componentWidth: string | null;
  order: number;
  legoInputId: number | null;
  syncDefaultAmbiguousInput: HowToHandleEmptyInput | null;
  syncDefaultValueJson: string | null;
  syncDescription: string | null;
};
export enum CmdTemplateChange {
  sync_creation = "sync_creation",
  generic_update = "generic_update",
  run_creds_update = "run_creds_update",
  sync_creds_update = "sync_creds_update",
}
export enum HowToHandleEmptyInput {
  emptyString = "emptyString",
  explicitNull = "explicitNull",
  omit = "omit",
  nullNone = "nullNone",
}
export type InstanceSchemaElementOnly = {
  // IMPORTANT NOTE: See params.initElement if you're making any changes here
  // specific to an instance in a kit, (in most settings) read/write
  defaultValue: SchemaElementValue;
  defaultAmbiguousInput: HowToHandleEmptyInput | null;
  defaultValueSource: "sync" | "user";
  useDefaultValue: boolean;
  lockDefaultValue: boolean; // really lockDefaultValueOrVariable
  hidden: boolean;
  note: string;
  tooltip: string;
  manuallyAdded: boolean;
  uuid: string; // this gets created on initElement
  // it does NOT get created on sync (I don't think we really need it on the backend)
  // this avoided having to write a data migration for the schema column, which is a little hairy
  // it's unique for a session, but we don't save it back to the database
  legoInstanceInputId: number;
  variable: string | null;
  placeholder: string | null;
  dropdownId: number | null;
};
export type InstanceSchemaElement = LegoSchemaElement &
  InstanceSchemaElementOnly;

export type KitRunSchemaElementOnly = {
  // IMPORTANT NOTE: See params.initElement and kitRunSchemaElementFields if you're making any changes here
  listElements: KitRunSchemaElement[] | null;
  value: SchemaElementValue;
  isValid: boolean;
};
export type PartialKitRunSchemaElement = Partial<KitRunSchemaElement>;
export type KitRunSchemaElement = KitRunSchemaElementOnly &
  InstanceSchemaElement;

export enum UIType {
  dropdown = "dropdown",
  radio = "radio",
  checkboxes = "checkboxes",
  input = "input",
  multiselect = "multiselect",
}
export enum ReferenceType {
  column = "column",
  columnArray = "columnArray", // columnArray = old school KingKit for-each button
  schemaElement = "schemaElement",
}
export enum DropdownDollType {
  hardcoded = "hardcoded",
  cmdOutput = "cmdOutput",
}
export interface DropdownDollHardcoded {
  type: DropdownDollType.hardcoded;
  dropdownId: number;
  name: string | null;
  enum: string[];
  // These must be null in this case
  valueCol: null;
  displayCol: null;
  kit: null;
  cacheInterval: null;
  componentId: "b-select" | "b-autocomplete" | "b-radio";
}

export interface DropdownDollCmdOutput {
  type: DropdownDollType.cmdOutput;
  dropdownId: number;
  name: string | null;
  // These must NOT be null in this case
  valueCol: EDropdownGET_OutputColumn;
  displayCol: EDropdownGET_OutputColumn_2;
  kit: EDropdownGET_CmdInstance;
  enum: null;
  cacheInterval: "weekly" | "monthly" | null;
  componentId: "b-select" | "b-autocomplete" | "b-radio";
}

// The new type is a union of the two possibilities
export type DropdownDoll = DropdownDollHardcoded | DropdownDollCmdOutput;
export function initDropdownDoll(dd: EDropdownGET): DropdownDoll {
  if (dd.enum) {
    return {
      type: DropdownDollType.hardcoded,
      dropdownId: dd.dropdownId,
      name: dd.name,
      enum: JSON.parse(dd.enum),
      valueCol: null,
      displayCol: null,
      kit: null,
      cacheInterval: null,
      componentId: dd.componentId as "b-select" | "b-autocomplete" | "b-radio",
    };
  } else if (dd.kit && dd.valueCol && dd.displayCol) {
    return {
      type: DropdownDollType.cmdOutput,
      dropdownId: dd.dropdownId,
      name: dd.name,
      valueCol: dd.valueCol,
      displayCol: dd.displayCol,
      kit: dd.kit,
      enum: null,
      cacheInterval: dd.cacheInterval as "weekly" | "monthly" | null,
      componentId: dd.componentId as "b-select" | "b-autocomplete" | "b-radio",
    };
  }
  throw new Error(
    "Invalid dropdown type - dd.enum is false but dd.kit/dd.valueCol/dd.displayCol is too"
  );
}

export enum SyncStageIState {
  posting_sync_stage_i = "posting_sync_stage_i",
  error = "error",
  posting_sync_stage_ii = "posting_sync_stage_ii",
  completed = "completed",
}
export enum SyncStageIIState {
  posting = "posting",
  error = "error",
  completed = "completed",
}
export enum SyncType {
  addition = "addition",
  deletion = "deletion",
  undeletion = "undeletion",
  update = "update",
}
export interface SyncStageIIDoll {
  id: string | number;
  name: string;
  isHidden: boolean;
  state: SyncStageIIState;
  response: SyncStageII;
  syncType: SyncType;
}
export enum ParamSource {
  postman = "postman",
  openapi = "openapi",
  mssql = "mssql",
  python_static_analysis = "python_static_analysis",
  r_static_analysis = "r_static_analysis",
  unavailable = "unavailable",
  manual = "manual",
}
export enum TypeSystem {
  python = "python",
  r = "r",
  mssql = "mssql",
  openapi = "openapi",
}
export interface TwoStageSyncWrapper {
  credsId: number;
  displayName: string;
  state: SyncStageIState;
  response: SyncStageI;
  additions: Record<string, SyncStageIIDoll>;
  deletions: Record<string, SyncStageIIDoll>;
  updates: Record<string, SyncStageIIDoll>;
  undeletions: Record<string, SyncStageIIDoll>;
}

export interface RunResultDoll {
  runResult: ERunGET | EKitRunGET_Run;
  frontendState: string;
  runrams: any;
  runOrder: number;
  runId: number | null;
}

export interface MostRecentRunWrapper {
  instanceId: number;
  kitInstanceId: number;
  runResults: RunResultDoll;
  cols: string[]; // type this
  transformedData: any[]; // type this
}

export type ApiQueryParams = {
  filter?: string | string[];
  sortDesc?: string;
  sort?: string;
  page?: number;
  per_page?: number;
};
export type ApiRequestOptions = {
  orgPath?: boolean;
  query?: ApiQueryParams;
  customQuery?: Record<string, any>;
  customHeaders?: Record<string, string>;
  timeout?: number;
  orgId?: string;
  isList?: boolean;
  enforceOrder?: boolean;
  debug?: boolean;
  customBody?: any;
  skipToken?: boolean;
};
export enum HttpMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
  PATCH = "PATCH",
}
export type ApiRequest = {
  method: HttpMethod;
  path: string;
  body?: any;
  options: ApiRequestOptions;
  callback?: (response: any) => any;
};
